import type { WeeklyReview as Review } from '@aj/core';
import { Box, Button, ChipGroup, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { useActions } from '../../data/hooks.js';
import { useT } from '../../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * The weekly review (§48 #6) — where the loop closes.
 *
 * Everything else in this app records; this is the only place that READS the
 * record back and changes the plan because of it. "Evidence into a better
 * promise" is a claim about this component specifically.
 *
 * Three deliberate choices:
 *
 * It opens with what was KEPT. A review that leads with failure is an audit,
 * and audits get avoided until they are abandoned.
 *
 * It names exactly ONE habit. A list of five things to fix is a list nobody
 * acts on, and the widest gap is the one worth a minute of thought.
 *
 * It ends on a question, not a score, and the answer goes to the goal's
 * obstacle — the same field the coach fills after a miss. Two routes to one
 * place, because an obstacle written down twice from different angles is the
 * one most likely to be true.
 */

/** Offered as one tap, because composing is harder work than choosing and a
 *  blank box on a Friday evening gets closed rather than filled. */
const OBSTACLES: MessageKey[] = [
  'coach.obstacle.phone',
  'coach.obstacle.tired',
  'coach.obstacle.busy',
  'coach.obstacle.unclear',
];

export type WeeklyReviewProps = {
  review: Review;
  goalId: string | null;
  onDone: () => void;
};

export function WeeklyReview({ review, goalId, onDone }: WeeklyReviewProps) {
  const t = useT();
  const actions = useActions();
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Box className="rounded-xl border border-line bg-sunken p-4">
        <VStack gap={3} align="start">
          <Text variant="overline" tone="subtle">
            {t('weekly.title')}
          </Text>
          <Text variant="body" tone="ink" className="text-pretty">
            {t('weekly.invite', { kept: review.kept, promised: review.promised })}
          </Text>
          <Button size="sm" onClick={() => setOpen(true)}>
            {t('weekly.start')}
          </Button>
        </VStack>
      </Box>
    );
  }

  const record = (obstacle: string) => {
    if (goalId !== null && obstacle) actions.editGoal(goalId, { obstacle });
    setOpen(false);
    onDone();
  };

  return (
    <Box className="rounded-xl border border-line p-4">
      <VStack gap={4} align="start">
        <Text variant="overline" tone="subtle">
          {t('weekly.title')}
        </Text>

        {/* What was kept, first. */}
        <VStack gap={1}>
          <Text variant="heading" tone="ink" numeric>
            {t('weekly.kept', { kept: review.kept, promised: review.promised })}
          </Text>
          {review.delta !== null && (
            <Text variant="caption" tone="muted" numeric>
              {t(review.delta >= 0 ? 'weekly.up' : 'weekly.down', {
                value: Math.abs(Math.round(review.delta * 100)),
              })}
            </Text>
          )}
        </VStack>

        {/* Exactly one habit to think about. */}
        {review.slipped && (
          <Text variant="body" tone="ink" className="text-pretty">
            {t('weekly.slipped', {
              name: review.slipped.name,
              kept: review.slipped.kept,
              promised: review.slipped.promised,
            })}
          </Text>
        )}

        {goalId !== null && (
          <VStack gap={2} align="start" className="w-full">
            <Text variant="label" tone="ink">
              {t('weekly.whatGotInTheWay')}
            </Text>
            <ChipGroup
              label={t('weekly.whatGotInTheWay')}
              options={OBSTACLES.map((key) => ({ value: t(key), label: t(key) }))}
              onSelect={record}
            />
            {review.obstacle !== null && (
              <Text variant="caption" tone="subtle">
                {t('weekly.lastTime', { obstacle: review.obstacle })}
              </Text>
            )}
          </VStack>
        )}

        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setOpen(false);
            onDone();
          }}
        >
          {t('weekly.finish')}
        </Button>
      </VStack>
    </Box>
  );
}
